import React, { useState, useEffect } from 'react';
import profileImage from '../assets/DSC_2130-demo.png';
import resumePDF from '../assets/SABARISAN.pdf';
import HireMePopup from './HireMePopup';

const Hero = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const titles = [
      'Front End Developer',
      'PHP Developer',
      'Full Stack Developer',
      'Web Developer'
    ];
    
    const currentTitle = titles[currentIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentTitle.length) {
          setDisplayText(currentTitle.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentIndex((prevIndex) => (prevIndex + 1) % titles.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [displayText, currentIndex, isDeleting]);

  const handleDownloadResume = () => {
    const link = document.createElement('a');
    link.href = resumePDF;
    link.download = 'Sabarisan_P_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="relative overflow-hidden min-h-screen flex items-center py-8">
      {/* Split Background */}
      <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2">
        <div className="bg-black"></div>
        <div className="bg-[#3d1a40]"></div>
      </div>

      {/* Animated Shapes */}
      <div className="absolute top-8 right-8 w-4 h-4 border-2 border-pink-500 rotate-45 animate-spin z-0" />
      <div className="absolute bottom-8 left-8 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[10px] border-l-transparent border-r-transparent border-b-yellow-400 animate-bounce z-0" />
      <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-ping z-0" />
      <div className="absolute bottom-1/3 right-1/4 w-3 h-3 bg-pink-500 rounded-full animate-pulse z-0" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-8 min-h-[80vh]">
          {/* Left Text */}
          <div className="text-white text-center md:text-left animate-fade-in-up order-2 md:order-1">
            <p className="text-yellow-400 italic mb-2 animate-fade-in">Hello,</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              I'm <span className="text-white">Sabarisan P</span> <br />
              a <span className="text-yellow-400 min-h-[1.2em] inline-block">
                {displayText}
                <span className="animate-pulse">|</span>
              </span>
            </h1>
            <p className="text-gray-300 mb-6 text-sm md:text-base italic animate-fade-in-up max-w-lg mx-auto md:mx-0" style={{ animationDelay: '0.5s' }}>
              Detail-oriented and highly organized logistics professional with 3 years of
              experience in code handling, freight coordination, and end-to-end supply
              chain operations. Skilled in PHP development, RESTful API integration,
              database management, and full stack development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
              <button
                onClick={() => setShowPopup(true)}
                className="border border-pink-500 text-white px-6 py-3 rounded-lg hover:bg-pink-500 transition-all font-semibold transform hover:scale-105 hover:shadow-lg"
              >
                Hire Me →
              </button>
              <button
                onClick={handleDownloadResume}
                className="bg-yellow-400 text-black px-6 py-3 rounded-lg hover:bg-yellow-500 transition-all font-semibold transform hover:scale-105 hover:shadow-lg"
              >
                Download Resume
              </button>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative flex justify-center md:justify-end items-center h-full animate-fade-in-right order-1 md:order-2">
            <div className="relative w-full max-w-sm md:max-w-md lg:max-w-lg mx-auto">
              {/* Background glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-yellow-400/20 rounded-full blur-3xl animate-pulse"></div>
              
              {/* Main image container */}
              <div className="relative z-10 flex justify-center">
                <img
                  src={profileImage}
                  alt="Sabarisan P - Full Stack Developer"
                  className="w-full h-auto object-contain drop-shadow-2xl transform hover:scale-105 transition-all duration-500 rounded-lg"
                  style={{
                    filter: 'drop-shadow(0 25px 50px rgba(236, 72, 153, 0.3))',
                    maxHeight: '70vh',
                    minHeight: '300px'
                  }}
                />
              </div>
              
              {/* Enhanced floating elements */}
              <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 w-8 h-8 md:w-10 md:h-10 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-bounce opacity-90 shadow-lg"></div>
              <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 w-6 h-6 md:w-8 md:h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full animate-pulse opacity-90 shadow-lg"></div>
              <div className="absolute top-1/4 -left-2 md:-left-4 w-4 h-4 md:w-6 md:h-6 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full animate-float opacity-80"></div>
              <div className="absolute bottom-1/4 -right-2 md:-right-4 w-3 h-3 md:w-4 md:h-4 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-ping opacity-70"></div>
              
              {/* Decorative rings */}
              <div className="absolute inset-0 border-2 border-pink-500/30 rounded-full animate-spin" style={{ animationDuration: '10s' }}></div>
              <div className="absolute inset-2 md:inset-4 border border-yellow-400/20 rounded-full animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup Component */}
      <HireMePopup show={showPopup} onClose={() => setShowPopup(false)} />
    </section>
  );
};

export default Hero;
